import { useState, useEffect } from "react";
import { Button } from "@component/button";
import { FileTextIcon, Loader2Icon } from "lucide-react";
import { getMyContractOffers } from "@service/contract";
import { ContractOffer } from "@type/contract";
import { ContractDetail } from "./contract-detail";
import { ContractItem } from "@component/contract-item";
import LoadingBox from "@component/loading-box";

interface Props {
  onGoToJobMatch: () => void;
}

export function ContractList(props: Props) {
  const [showContractDetails, setShowContractDetails] = useState(false);
  const [selectedContract, setSelectedContract] = useState<ContractOffer | undefined>(undefined);
  const [contracts, setContracts] = useState<ContractOffer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContracts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const contracts = await getMyContractOffers();
      setContracts(contracts || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch contracts."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  const handleContractSelect = (contract: ContractOffer) => {
    setSelectedContract(contract);
    setShowContractDetails(true);
  };

  const handleBackToContracts = () => {
    fetchContracts();
    setShowContractDetails(false);
    setSelectedContract(undefined);
  };

  if (error) {
    return (
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex items-center justify-center h-64">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
    // className="bg-white rounded-lg p-6 border border-gray-200"
    >
      <div className="mb-3 sm:mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Contract Offers
        </h2>
        <p className="text-gray-600">
          Review and manage your job contract offers.
        </p>
      </div>

      {showContractDetails && selectedContract ? (
        <ContractDetail
          contract={selectedContract}
          onReload={() => {
            fetchContracts()
            setShowContractDetails(false);
            setSelectedContract(undefined);
          }}
          onBack={handleBackToContracts}
        />
      ) : (
        <div className="space-y-4">
          {isLoading ? (
            <LoadingBox />
          ) : (
            <>
              {(contracts || []).length > 0 ? (
                contracts.map((contract) => (
                  <ContractItem
                    key={contract.id}
                    contract={contract}
                    onClick={handleContractSelect}
                  />
                ))
              ) : (
                <div className="text-center py-10 bg-gray-50 rounded-lg">
                  <FileTextIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    No contract offers yet.
                  </h3>
                  <p className="text-gray-600 mb-4">
                    When you receive job offers, they'll appear here.
                  </p>
                  <Button
                    variant="outline"
                    onClick={props.onGoToJobMatch}
                    className="mx-auto"
                  >
                    View Job Matches
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
